import { Button, IconSize, ReloadIcon } from '@protoss/ui';
import * as React from 'react';
import { PageErrorState } from '../../../components/shared/PageErrorState';
import { SplitPaneLayout } from '../../../components/SplitPaneLayout';
import { useJobRun } from '../hooks/useJobRun';
import { useJobRuns } from '../hooks/useJobRuns';
import type { JobRunStatus } from '../types/jobs';
import { JobFilterBar } from './JobFilterBar';
import { JobRunDetails } from './JobRunDetails';
import { JobRunTable } from './JobRunTable';

const PAGE_SIZE = 20;

interface JobsPageProps {
  projectUuid: string;
  /** Breadcrumb label shown above the page title, e.g. "ODT > CMS Platform" */
  breadcrumb?: string;
}

/**
 * Jobs page - full-page list + resizable detail pane.
 *
 * Layout:
 *   Page header:  breadcrumb + "Job Runs" title + Refresh button (reuses ss-panel__header pattern)
 *   Shell:        one bordered rounded container
 *     Left pane:  filter bar + paginated table
 *     Divider:    resizable vertical divider (only when a run is selected)
 *     Right pane: job run detail + logs (only when a run is selected)
 */
export function JobsPage({ projectUuid, breadcrumb }: JobsPageProps) {
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState<JobRunStatus | undefined>(undefined);
  const [selectedUuid, setSelectedUuid] = React.useState<string | null>(null);

  const { data, loading, error, refresh } = useJobRuns(projectUuid, {
    page,
    pageSize: PAGE_SIZE,
    status: statusFilter,
  });

  const { data: detail, loading: detailLoading } = useJobRun(projectUuid, selectedUuid);

  function handleStatusChange(s: JobRunStatus | undefined) {
    setStatusFilter(s);
    setPage(1);
  }

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 1;

  return (
    <div className="jobs-page">
      {/* Page header - breadcrumb + title + Refresh button */}
      <header className="ss-panel__header">
        <div className="ss-panel__header-left">
          {breadcrumb && <p className="ss-panel__breadcrumb">{breadcrumb}</p>}
          <h1 className="ss-panel__title">Job Runs</h1>
        </div>
        <div className="ss-panel__header-actions">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void refresh()}
            disabled={loading}
            aria-label="Refresh job list"
          >
            <ReloadIcon
              width={IconSize.sm}
              height={IconSize.sm}
              aria-hidden="true"
              className={`ss-panel__action-icon${loading ? ' ss-panel__action-icon--spinning' : ''}`}
            />
            Refresh
          </Button>
        </div>
      </header>

      <SplitPaneLayout
        filterArea={<JobFilterBar status={statusFilter} onStatusChange={handleStatusChange} />}
        loading={loading}
        hasError={!!error}
        page={page}
        totalPages={totalPages}
        rowCount={data?.jobs.length}
        onPageChange={setPage}
        selectedId={selectedUuid}
        detailTitle={
          !detailLoading && detail ? (
            <div className="jobs-detail__title-wrap">
              <span className="jobs-detail__name">{detail.jobName}</span>
              {detail.relatedResourceName && (
                <span className="jobs-detail__resource">{detail.relatedResourceName}</span>
              )}
            </div>
          ) : null
        }
        detailContent={
          detailLoading ? (
            <p className="jobs-page__loading">Loading run details…</p>
          ) : detail ? (
            <JobRunDetails projectUuid={projectUuid} detail={detail} />
          ) : null
        }
        onCloseDetail={() => setSelectedUuid(null)}
        defaultDetailWidth={560}
        maxDetailWidth={860}
        dividerAriaLabel="Resize job detail pane"
      >
        {loading && <p className="jobs-page__loading">Loading…</p>}
        {error && <PageErrorState message={error} onRetry={() => void refresh()} />}
        {!loading && !error && data && (
          <JobRunTable jobs={data.jobs} selectedUuid={selectedUuid} onSelect={setSelectedUuid} />
        )}
      </SplitPaneLayout>
    </div>
  );
}
